import { ArrowRight, Compass, HandHeart, Lightbulb, Move3d, UsersRound } from "lucide-react";
import { Link } from "wouter";
import { CtaBand } from "@/components/site/CtaBand";
import { PageHero } from "@/components/site/PageHero";
import { SiteLayout } from "@/components/site/SiteLayout";
import { carePrinciples, coreValues, teamMembers } from "@/content/team";

const principleIcons = [UsersRound, Move3d, Lightbulb, Compass, HandHeart, Lightbulb];

const teamPortraits: Record<string, string> = {
  "Alexis Larson": "/manus-storage/alexis-larson-transparent_f0586134.webp",
  "Lauren Blando": "/manus-storage/lauren-blando-preview_b62f452a.webp",
  "Kacie Beebe": "/manus-storage/kacie-beebe-preview_385905b8.webp",
  "Jennifer Caswell": "/manus-storage/jennifer-caswell-preview_8483f8a4.webp",
  "Michelle Ahern": "/manus-storage/michelle-ahern-preview_0c2400bc.webp",
  "Beth Monger": "/manus-storage/beth-monger-preview_f778df2b.webp",
};

export default function OurTeam() {
  return (
    <SiteLayout>
      <PageHero
        currentPage="Our Team"
        eyebrow="Serving Overland Park since 2007"
        title={<>A team invested in <em>how you move.</em></>}
        description="CORE grew from a belief that physical therapy should be personal, collaborative, and connected to the life a person wants to live—not limited to a diagnosis or body part."
        aside={
          <div className="hero-quote-card">
            <span>Our story began on a trail.</span>
            <blockquote>“We opened our doors in April 2007, inspired by the idea that thoughtful movement care could create a different path forward.”</blockquote>
          </div>
        }
      />

      <section className="page-section page-section-white" aria-labelledby="story-title">
        <div className="container story-grid">
          <div>
            <p className="section-kicker">Our story</p>
            <h2 id="story-title">An idea formed in Colorado. A practice built in Overland Park.</h2>
          </div>
          <div className="prose-lead">
            <p>In the fall of 2006, CORE’s founders were in Colorado for continuing education followed by a day of hiking. During that trip, they imagined a practice that would analyze movement carefully, treat the whole person, and continue evolving whenever patients needed a better solution. CORE Strategies Physical Therapy opened in April 2007.</p>
            <p>That trail-minded spirit remains part of the practice today. The team looks for the movement patterns, tissue restrictions, strength deficits, coordination challenges, and nervous-system changes that may be limiting a person’s options. Then they build a plan around the person’s history, needs, and destination.</p>
          </div>
        </div>
      </section>

      <section className="page-section team-section" aria-labelledby="team-title">
        <div className="container">
          <div className="section-heading centered">
            <p className="section-kicker">Meet the team</p>
            <h2 id="team-title">Knowledgeable care. Human connection.</h2>
            <p>Each member of CORE contributes to the experience—from clinical reasoning and movement coaching to scheduling, billing, and the personal welcome at the front desk.</p>
          </div>
          <div className="team-grid">
            {teamMembers.map((member) => (
              <article className="team-card" key={member.name}>
                {teamPortraits[member.name] ? (
                  <div className="team-portrait">
                    <img
                      src={teamPortraits[member.name]}
                      alt={member.name}
                      width="1000"
                      height="1289"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="team-monogram" aria-hidden="true">{member.name.split(" ").map((part) => part[0]).join("")}</div>
                )}
                <div className="team-card-head">
                  <h3>{member.name}{member.credentials ? <span>, {member.credentials}</span> : null}</h3>
                  <p>{member.role}</p>
                  {member.specialties ? <div className="specialty-tags">{member.specialties.map((specialty) => <span key={specialty}>{specialty}</span>)}</div> : null}
                </div>
                <div className="team-bio">{member.bio.map((paragraph) => <p key={paragraph.slice(0, 36)}>{paragraph}</p>)}</div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="page-section values-section" aria-labelledby="values-title">
        <div className="container">
          <div className="values-heading">
            <p className="section-kicker light">Our CORE values</p>
            <h2 id="values-title">The ideas that guide the work.</h2>
          </div>
          <div className="value-grid">
            {coreValues.map((value, index) => <article key={value.title}><span>0{index + 1}</span><h3>{value.title}</h3><p>{value.body}</p></article>)}
          </div>
        </div>
      </section>

      <section className="page-section page-section-white" aria-labelledby="principles-title">
        <div className="container">
          <div className="section-heading centered">
            <p className="section-kicker">Our care model</p>
            <h2 id="principles-title">What you can expect from CORE.</h2>
          </div>
          <div className="principle-grid">
            {carePrinciples.map(([title, body], index) => {
              const Icon = principleIcons[index];
              return <article key={title}><Icon aria-hidden="true" /><h3>{title}</h3><p>{body}</p></article>;
            })}
          </div>
          <div className="mission-card">
            <div><span>Mission</span><p>To deliver compassionate care and meaningful, lasting strategies for musculoskeletal conditions by evaluating the whole person, identifying the core problem, and tailoring the plan to individual needs.</p></div>
            <div><span>Vision</span><p>To be a trusted lifelong resource for movement, function, performance, and wellness while helping raise the standard of care through collaboration, learning, and innovation.</p></div>
          </div>
        </div>
      </section>

      <CtaBand title="Meet the team behind your next step." body="Call the office or send an inquiry to begin a conversation about your goals." />
    </SiteLayout>
  );
}
